
import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import './Components/RealtorLandingPage.css';
import './Components/ProfessionalLandingPage.css';
import { auth, db, functions } from './firebase-config';
import { httpsCallable } from 'firebase/functions';
import { 
  onAuthStateChanged, 
  signOut, 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence
} from "firebase/auth";
import { doc, setDoc, getDoc, collection, addDoc, updateDoc, getDocs, query, where } from "firebase/firestore";
import { HelmetProvider } from 'react-helmet-async';

import TenantDashboard from './Components/TenantDashboard';
import OwnerDashboard from './Components/OwnerDashboard';
import AcceptInvitationPage from './Components/AcceptInvitationPage';
import OwnerRateProfessionalPage, { PENDING_OWNER_RATE_JOB_SESSION_KEY } from './Components/OwnerRateProfessionalPage';
import OwnerStaffInvitePage, {
  PENDING_OWNER_STAFF_INVITE_TOKEN_KEY,
} from './Components/OwnerStaffInvitePage';
import AdminDashboard from './Components/AdminDashboard';
// Admin feedback route/component removed (not present in repo)
// TenantListPage is rendered inside OwnerDashboard now
import ProfessionalDashboard from './Components/ProfessionalDashboard';
import ProfessionalJoinWizard from './Components/ProfessionalJoinWizard';
import RealtorDashboard from './Components/RealtorDashboard';
import RealtorLandingPage from './Components/RealtorLandingPage';
import ProfessionalLandingPage from './Components/ProfessionalLandingPage';
import LandingPage from './Components/LandingPage';
import AboutUs from './Components/AboutUs';
import PrivacyPage from './Components/PrivacyPage';
import SubprocessorsPage from './Components/SubprocessorsPage';
import TermsPage from './Components/TermsPage';
import ContactPage from './Components/ContactPage';
import FeedbackModal from './Components/FeedbackModal';
import { getStoredDashboardTheme, applyDashboardThemeToDocument } from './utils/dashboardTheme';

import { BrowserRouter as Router, Route, Routes, useNavigate, useLocation, Navigate, useSearchParams } from 'react-router-dom';
import { OWNER_PRO_INVITE_TOKEN_KEY } from './utils/professionalOnboardingConstants';

const GA_MEASUREMENT_ID = 'G-D6L84N55K4';

/** Stashed when an unauthenticated user opens `/owner?requestId=…` from email; applied after owner login. */
const PENDING_OWNER_OPEN_MAINTENANCE_REQUEST_KEY = 'td_owner_open_request_id';

/**
 * Email invite links use `/professional?proInvite=…`. Anonymous users must reach join with the token;
 * signed-in non-pros should not be dropped on the wizard.
 */
function ProfessionalInviteEntryRedirect({ user, userData }) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('proInvite')?.trim();

  if (user && !userData) {
    return null;
  }
  if (user && userData?.role !== 'professional') {
    return <Navigate to="/" replace />;
  }

  if (token) {
    try {
      sessionStorage.setItem(OWNER_PRO_INVITE_TOKEN_KEY, token);
    } catch (e) {
      console.warn('sessionStorage owner pro invite token:', e);
    }
    return (
      <Navigate
        to={`/for-professionals/join?proInvite=${encodeURIComponent(token)}`}
        replace
      />
    );
  }
  return <Navigate to="/" replace />;
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * GA4 page views for every route (including first paint). index.html uses send_page_view: false
   * so this is the single source of truth and works with React Router + Strict Mode.
   */
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    const pagePath = `${location.pathname}${location.search}`;
    window.gtag('config', GA_MEASUREMENT_ID, { page_path: pagePath });
  }, [location.pathname, location.search]);

  const [showAuth, setShowAuth] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  // --- AUTH STATES ---
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null); 
  
  // --- URL PARAMS (for tenant signup from share link) ---
  const [urlParams, setUrlParams] = useState({});
  const [unitDetails, setUnitDetails] = useState(null); // Store fetched unit details
  
  // --- FORM STATES (LOGIN/SIMPLE SIGNUP) ---
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('tenant');
  const [name, setName] = useState('');
  const [unit, setUnit] = useState('');
  const [phone, setPhone] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [authMessage, setAuthMessage] = useState('');
  const [authMessageType, setAuthMessageType] = useState('');
  const [loginError, setLoginError] = useState(false);
  
  // --- OWNER MULTI-STAGE SIGNUP STATES ---
  const [, setOwnerSignupStage] = useState(1); // 1, 2, or 3 (reset on register toggle)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [homeAddress, setHomeAddress] = useState('');
  const [, setNumUnits] = useState('');
  const [units, setUnits] = useState([]);

  const hasTenantInviteQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return Boolean(params.get('unitId') && params.get('ownerId'));
  }, [location.search]);

  const hasOwnerInviteQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return Boolean(params.get('ownerInvitationId')?.trim());
  }, [location.search]);

  const hasStaffInviteQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return Boolean(params.get('staffInviteToken')?.trim());
  }, [location.search]);

  const shouldShowAuthForm =
    Boolean(user)
      ? false
      : showAuth ||
        hasTenantInviteQuery ||
        hasOwnerInviteQuery ||
        hasStaffInviteQuery ||
        location.pathname === '/login';

  // Tenant invite links: parse query whenever search changes (and sync Firestore unit details)
  useEffect(() => {
    if (user) return;
    const params = new URLSearchParams(location.search);
    const unitId = params.get('unitId');
    const ownerId = params.get('ownerId');
    const applicantId = params.get('applicantId');
    const tenantName = params.get('tenantName');
    const tenantEmail = params.get('tenantEmail');
    const tenantPhone = params.get('tenantPhone');

    if (!unitId || !ownerId) {
      setUrlParams((prev) => {
        if (!prev.unitId && !prev.ownerId) return prev;
        return {};
      });
      setUnitDetails((prev) => (prev ? null : prev));
      return;
    }

    setUrlParams({
      unitId,
      ownerId,
      ...(applicantId ? { applicantId } : {}),
    });
    setUnit(unitId);
    setRole('tenant');
    setIsNewUser(true);
    setShowAuth(true);

    if (tenantName) {
      setName(tenantName.replace(/\+/g, ' '));
    }
    if (tenantEmail) {
      setEmail(tenantEmail.trim());
    }
    if (tenantPhone) {
      setPhone(tenantPhone.trim());
    }

    const fetchUnitDetails = async () => {
      try {
        const preview = httpsCallable(functions, 'getTenantInviteUnitPreview');
        const res = await preview({ unitId: unitId.trim(), ownerId: ownerId.trim() });
        const unitData = res.data || {};
        setUnitDetails({
          unitNumber: unitData.unitNumber,
          address: unitData.address,
          monthlyRent: unitData.monthlyRent,
        });
        if (unitData.invitedTenant?.name) {
          setName(unitData.invitedTenant.name);
        }
        if (unitData.invitedTenant?.email) {
          setEmail(unitData.invitedTenant.email);
        }
        if (unitData.invitedTenant?.phone) {
          setPhone(unitData.invitedTenant.phone);
        }
        setUrlParams((prev) => ({
          ...prev,
          unitNumber: unitData.unitNumber,
          address: unitData.address,
          monthlyRent: unitData.monthlyRent,
        }));
      } catch (error) {
        console.error('Error fetching unit details:', error);
        const code = error?.code || '';
        if (
          code === 'functions/not-found' ||
          code === 'functions/permission-denied' ||
          code === 'functions/failed-precondition' ||
          code === 'functions/invalid-argument'
        ) {
          alert(
            'Error: This unit link is invalid or the unit no longer exists. Please contact your property owner for a new link.'
          );
        } else {
          alert('Error loading unit details. Please refresh the page and try again.');
        }
      }
    };
    fetchUnitDetails();
  }, [location.search, user]);

  // Realtor → owner invite: open owner signup/signin with invitation id preserved for after auth
  useEffect(() => {
    if (user) return;
    const params = new URLSearchParams(location.search);
    const ownerInvitationId = params.get('ownerInvitationId')?.trim();
    if (!ownerInvitationId) return;

    try {
      sessionStorage.setItem('pendingOwnerInvitationId', ownerInvitationId);
    } catch (e) {
      console.warn('sessionStorage pendingOwnerInvitationId:', e);
    }
    setRole('owner');
    setIsNewUser(true);
    setShowAuth(true);
    if (window.location.hash !== '#auth') {
      window.history.replaceState(null, '', `${location.pathname}${location.search}#auth`);
    }
  }, [location.search, location.pathname, user]);

  // Owner staff invite: open owner signup/signin with token preserved for after auth → `/owner/staff-invite`
  useEffect(() => {
    if (user) return;
    const params = new URLSearchParams(location.search);
    const staffInviteToken = params.get('staffInviteToken')?.trim();
    if (!staffInviteToken) return;

    try {
      sessionStorage.setItem(PENDING_OWNER_STAFF_INVITE_TOKEN_KEY, staffInviteToken);
    } catch (e) {
      console.warn('sessionStorage pendingOwnerStaffInviteToken:', e);
    }
    const prefillEmail = params.get('email')?.trim();
    if (prefillEmail) {
      setEmail((prev) => (String(prev || '').trim() ? prev : prefillEmail));
    }
    setRole('owner');
    setIsNewUser(true);
    setShowAuth(true);
    if (window.location.hash !== '#auth') {
      window.history.replaceState(null, '', `${location.pathname}${location.search}#auth`);
    }
  }, [location.search, location.pathname, user]);

  // Professional landing: ?signup=professional&email=… opens signup with role + optional email prefill
  useEffect(() => {
    if (user) return;
    const params = new URLSearchParams(location.search);
    if (params.get('signup') !== 'professional') return;

    setRole('professional');
    setIsNewUser(true);
    setShowAuth(true);
    const prefill = params.get('email')?.trim();
    if (prefill) {
      setEmail(prefill);
    }
    if (window.location.hash !== '#auth') {
      window.history.replaceState(null, '', `${location.pathname}${location.search}#auth`);
    }
  }, [location.search, location.pathname, user]);

  useEffect(() => {
    applyDashboardThemeToDocument(getStoredDashboardTheme());
  }, []);

  // --- HANDLE BROWSER BACK BUTTON FOR AUTH MODAL ---
  useEffect(() => {
    const handlePopState = () => {
      if (window.location.hash !== '#auth' && showAuth) {
        setShowAuth(false);
      } else if (window.location.hash === '#auth' && !showAuth) {
        setShowAuth(true);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showAuth]);
  // --- REFRESH LOGIC: FETCHES FRESH DATA FROM FIRESTORE ---
  const refreshUserData = async (uid) => {
    try {
      // Try to find user in role-specific collections
      const collections = ['TenantsData', 'OwnersData', 'ProfessionalsData', 'RealtorsData'];
      
      for (const collName of collections) {
        const userRef = doc(db, collName, uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setUserData(userSnap.data());
          return;
        }
      }
      // Fallback to old users collection if not found
      const userRef = doc(db, "users", uid);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        setUserData(userSnap.data());
      }
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
  };

  // --- BRAIN: LISTENS FOR LOGIN AND FETCHES PROFILE ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        // TEMPORARY SUPER ADMIN SETUP: Auto-upgrade the admin account
        if (currentUser.email === 'admin@tenantdispatch.com') {
          try {
            await updateDoc(doc(db, 'OwnersData', currentUser.uid), { isSuperAdmin: true });
          } catch (e) { console.error('Auto-admin setup failed:', e); }
        }
        // Fetch fresh data from Firestore on login
        await refreshUserData(currentUser.uid);
      } else {
        setUserData(null);
        const path = window.location.pathname;
        const sp = new URLSearchParams(window.location.search);
        const inviteOpen = sp.get('unitId') && sp.get('ownerId');
        const ownerInviteOpen = Boolean(sp.get('ownerInvitationId')?.trim());
        const staffInviteOpen = Boolean(sp.get('staffInviteToken')?.trim());
        const onOwnerAcceptPath = path.startsWith('/owner/accept-invite');
        const onOwnerRateJobPath = path.startsWith('/owner/rate-job/');
        const onOwnerStaffInvitePath = path.startsWith('/owner/staff-invite');
        if (
          !inviteOpen &&
          !ownerInviteOpen &&
          !staffInviteOpen &&
          !onOwnerAcceptPath &&
          !onOwnerRateJobPath &&
          !onOwnerStaffInvitePath &&
          path !== '/login'
        ) {
          setShowAuth(false);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Navigate to dashboard when userData is loaded, but only if not already on a subroute
  useEffect(() => {
    if (user && userData) {
      const role = userData.role;
      const currentPath = location.pathname;

      if (
        currentPath.startsWith('/owner/accept-invite') ||
        currentPath.startsWith('/owner/rate-job/') ||
        currentPath.startsWith('/owner/staff-invite')
      ) {
        return;
      }

      let pendingOwnerInvite = '';
      try {
        pendingOwnerInvite = sessionStorage.getItem('pendingOwnerInvitationId') || '';
      } catch (e) {
        /* ignore */
      }

      let pendingStaffInviteToken = '';
      try {
        pendingStaffInviteToken =
          sessionStorage.getItem(PENDING_OWNER_STAFF_INVITE_TOKEN_KEY) || '';
      } catch (e) {
        /* ignore */
      }

      // Only auto-redirect if on root or initial paths, don't redirect if on subroutes
      if (currentPath === '/' || currentPath === '' || currentPath === '/login') {
        if (role === 'owner') {
          let pendingRateJob = '';
          try {
            pendingRateJob = sessionStorage.getItem(PENDING_OWNER_RATE_JOB_SESSION_KEY) || '';
          } catch (e) {
            /* ignore */
          }
          if (pendingRateJob) {
            try {
              sessionStorage.removeItem(PENDING_OWNER_RATE_JOB_SESSION_KEY);
            } catch (e) {
              /* ignore */
            }
            navigate(`/owner/rate-job/${pendingRateJob}`, { replace: true });
          } else {
            let pendingOpenMaintenance = '';
            try {
              pendingOpenMaintenance =
                sessionStorage.getItem(PENDING_OWNER_OPEN_MAINTENANCE_REQUEST_KEY) || '';
            } catch (e) {
              /* ignore */
            }
            if (pendingOpenMaintenance) {
              try {
                sessionStorage.removeItem(PENDING_OWNER_OPEN_MAINTENANCE_REQUEST_KEY);
              } catch (e) {
                /* ignore */
              }
              navigate(
                `/owner?requestId=${encodeURIComponent(pendingOpenMaintenance)}`,
                { replace: true }
              );
            } else if (userData.isSuperAdmin) {
              navigate('/admin');
            } else if (pendingStaffInviteToken) {
              navigate(
                `/owner/staff-invite?token=${encodeURIComponent(pendingStaffInviteToken)}`,
                { replace: true }
              );
            } else if (pendingOwnerInvite) {
              navigate(`/owner/accept-invite/${pendingOwnerInvite}`, { replace: true });
            } else {
              navigate('/owner');
            }
          }
        } else if (role === 'professional') {
          navigate('/professional');
        } else if (role === 'tenant') {
          navigate('/tenant');
        } else if (role === 'realtor') {
          navigate('/realtor');
        }
      }
    }
  }, [user, userData, navigate, location.pathname]);

  // --- ACTIONS ---
  const handleLogin = async () => {
    try {
      setAuthMessage('');
      setLoginError(false);
      await setPersistence(
        auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence
      );
      const cred = await signInWithEmailAndPassword(auth, email, password);

      // Staff invite: if a user signs in without completing owner signup,
      // create a minimal authorized-user owner profile so they can accept the invite.
      try {
        const pendingStaffInviteToken =
          sessionStorage.getItem(PENDING_OWNER_STAFF_INVITE_TOKEN_KEY) || '';
        if (pendingStaffInviteToken && cred?.user?.uid) {
          const ownerRef = doc(db, 'OwnersData', cred.user.uid);
          const ownerSnap = await getDoc(ownerRef);
          if (!ownerSnap.exists()) {
            await setDoc(
              ownerRef,
              {
                email: (cred.user.email || email || '').trim(),
                role: 'owner',
                accountType: 'authorized_user',
                isAuthorizedUser: true,
                createdAt: new Date(),
              },
              { merge: true }
            );
          }
        }
      } catch (e) {
        console.warn('staff invite: ensure minimal OwnersData profile failed', e);
      }
      // Navigation will happen automatically via useEffect once userData is set
    } catch (error) {
      setLoginError(true);
      setAuthMessageType('error');
      setAuthMessage('Invalid email or password. Please try again.');
    }
  };

  const handlePasswordReset = async () => {
    if (!email.trim()) {
      setAuthMessageType('error');
      setAuthMessage('Enter your email address first, then click "Forgot password?" again.');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email.trim());
      setAuthMessageType('success');
      setAuthMessage(`Password reset email sent to ${email.trim()}. Check your inbox and spam folder.`);
    } catch (error) {
      setAuthMessageType('error');
      setAuthMessage(error.message);
    }
  };

  const handleBackToHome = () => {
    setShowAuth(false);
    setAuthMessage('');
    setAuthMessageType('');
    if (window.location.hash === '#auth') {
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
    }
    const sp = new URLSearchParams(window.location.search);
    if (
      (sp.get('unitId') && sp.get('ownerId')) ||
      sp.get('ownerInvitationId') ||
      sp.get('staffInviteToken') ||
      window.location.pathname === '/login'
    ) {
      navigate('/', { replace: true });
    }
  };

  const handleSignUp = async () => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      
      // Different signup logic based on role
      if (role === 'owner') {
        let pendingStaffInviteToken = '';
        try {
          pendingStaffInviteToken =
            sessionStorage.getItem(PENDING_OWNER_STAFF_INVITE_TOKEN_KEY) || '';
        } catch (e) {
          /* ignore */
        }
        const isStaffInviteSignup = Boolean(pendingStaffInviteToken);

        const trimmedName = String(name || '').trim();
        const nameParts = trimmedName ? trimmedName.split(/\s+/).filter(Boolean) : [];
        const inferredFirst = firstName || (nameParts.length ? nameParts[0] : '');
        const inferredLast = lastName || (nameParts.length > 1 ? nameParts.slice(1).join(' ') : '');

        // Owner: Create profile with all units
        const newOwnerProfile = {
          email: email,
          role: 'owner',
          firstName: inferredFirst,
          lastName: inferredLast,
          homeAddress: homeAddress,
          ...(isStaffInviteSignup ? { accountType: 'authorized_user', isAuthorizedUser: true } : {}),
          createdAt: new Date()
        };
        
        await setDoc(doc(db, 'OwnersData', userCredential.user.uid), newOwnerProfile);

        // Mark invitation as registered if owner was invited
        try {
          const invitationQuery = query(
            collection(db, 'invitations'),
            where('ownerEmail', '==', email.trim().toLowerCase())
          );
          const invitationSnap = await getDocs(invitationQuery);
          for (const invitationDoc of invitationSnap.docs) {
            const invitationData = invitationDoc.data();
            const nextStatus = ['connected', 'rejected'].includes(invitationData.status)
              ? invitationData.status
              : 'pending_owner_acceptance';
            await updateDoc(invitationDoc.ref, {
              ownerUid: userCredential.user.uid,
              status: nextStatus
            });
          }
        } catch (err) {
          console.error('Error updating invitation status:', err);
        }

        // Create unit documents (skip for authorized team member signups)
        if (!isStaffInviteSignup) {
          for (const unit of units) {
            await addDoc(collection(db, 'units'), {
              ownerId: userCredential.user.uid,
              address: unit.address,
              monthlyRent: unit.monthlyRent,
              unitNumber: unit.unitNumber,
              createdAt: new Date()
            });
          }
        }
        
        setUserData(newOwnerProfile);
        let pendingOwnerInvite = '';
        try {
          pendingOwnerInvite = sessionStorage.getItem('pendingOwnerInvitationId') || '';
        } catch (e) {
          /* ignore */
        }

        try {
          if (!isStaffInviteSignup) {
            const sendOwnerWelcome = httpsCallable(functions, 'sendOwnerSignupWelcome');
            await sendOwnerWelcome(
              pendingOwnerInvite ? { pendingInvitationId: pendingOwnerInvite } : {}
            );
          }
        } catch (welcomeErr) {
          console.error('sendOwnerSignupWelcome:', welcomeErr);
        }

        if (newOwnerProfile.isSuperAdmin) {
          navigate('/admin');
        } else if (pendingStaffInviteToken) {
          navigate(
            `/owner/staff-invite?token=${encodeURIComponent(pendingStaffInviteToken)}`
          );
        } else if (pendingOwnerInvite) {
          navigate(`/owner/accept-invite/${pendingOwnerInvite}`);
        } else {
          navigate('/owner');
        }
      } else if (role === 'tenant') {
        // Validate unit details are loaded
        const tenantUnitNumber = unitDetails?.unitNumber || urlParams.unitNumber;
        if (!tenantUnitNumber) {
          alert('Please wait for unit details to load before signing up.');
          return;
        }
        
        // Tenant: Create profile with unit from link
        const newTenantProfile = {
          email: email,
          role: 'tenant',
          fullName: name,
          phoneNumber: phone,
          unitId: urlParams.unitId,
          unitNumber: tenantUnitNumber,
          ownerId: urlParams.ownerId,
          createdAt: new Date()
        };
        
        await setDoc(doc(db, 'TenantsData', userCredential.user.uid), newTenantProfile);

        const applicantIdFromUrl = new URLSearchParams(window.location.search).get('applicantId');
        if (applicantIdFromUrl) {
          try {
            const finalize = httpsCallable(functions, 'finalizeConvertedTenantRegistration');
            await finalize({ applicantId: applicantIdFromUrl });
          } catch (linkErr) {
            console.error('finalizeConvertedTenantRegistration:', linkErr);
            const msg =
              linkErr?.message ||
              'Could not link your account to the approved application.';
            alert(
              `Account created, but linking failed: ${msg}\n\nTry signing in, or contact your property owner for a new invite link.`
            );
          }
        }

        await refreshUserData(userCredential.user.uid);
        navigate('/tenant');
      } else if (role === 'professional') {
        // Professional: Create profile
        const newProfProfile = {
          email: email,
          role: 'professional',
          fullName: name,
          companyName: unit,
          phoneNumber: phone,
          createdAt: new Date()
        };
        
        await setDoc(doc(db, 'ProfessionalsData', userCredential.user.uid), newProfProfile);
        setUserData(newProfProfile);
        navigate('/professional');
      } else if (role === 'realtor') {
        // Realtor: Create profile in RealtorsData
        const newRealtorProfile = {
          email: email,
          role: 'realtor',
          fullName: name,
          licenseNumber: unit, // reuse 'unit' field for license number
          phoneNumber: phone,
          createdAt: new Date()
        };
        
        await setDoc(doc(db, 'RealtorsData', userCredential.user.uid), newRealtorProfile);
        setUserData(newRealtorProfile);
        navigate('/realtor');
      }
    } catch (error) { 
      alert(error.message); 
    }
  };

  // Determine what content to show
  const renderContent = () => {
    if (user) {
      // Logged in — navigate to appropriate dashboard based on role
      if (!userData) return null; // Still loading
      
      // Use effect to handle navigation after userData loads
      return null; // Routes will handle display
    } else if (!shouldShowAuthForm) {
      return null; // Handled by routes
    } else {
      const tenantFromApplication = Boolean(urlParams.applicantId && unitDetails);
      const readOnlyPrefilled = (v) => tenantFromApplication && String(v || '').trim() !== '';
      const readOnlyInputStyle = { background: '#f1f5f9', color: '#334155', cursor: 'not-allowed' };
      const staffInviteSignup = Boolean(hasStaffInviteQuery && isNewUser);

      // Show the login/signup form when 'Get Started' is clicked
      return (
        <div className="App auth-page">
          <div className="auth-shell">
            <div className="auth-brand">
              <img
                src="/tenant-dispatch-icon-512.png"
                alt=""
                className="auth-brand-logo"
              />
              <h1 className="auth-brand-name">Tenant Dispatch</h1>
              <p className="auth-brand-tagline">
                Property maintenance, automated. Sign in to manage requests, units, and your team.
              </p>
            </div>
            <div className="form-card auth-card">
              <button
                type="button"
                className="back-home-button"
                onClick={handleBackToHome}
              >
                ← Back to home
              </button>
          {/* === TENANT SIGNUP VIA LINK === */}
          {urlParams.unitId && urlParams.ownerId && isNewUser && role === 'tenant' ? (
            <form onSubmit={(e) => { e.preventDefault(); handleSignUp(); }}>
              <h2 className="form-header">Sign Up as Tenant</h2>
              <p style={{color: '#666', marginBottom: '20px', textAlign: 'center'}}>You've been invited to join a property management unit</p>
              <div style={{background: '#e7f3ff', padding: '15px', borderRadius: '5px', marginBottom: '15px', borderLeft: '4px solid #007bff'}}>
                <p style={{margin: '0 0 10px 0', fontWeight: 'bold'}}>Your Unit Assignment:</p>
                <p style={{margin: '5px 0', color: '#333'}}><strong>Unit:</strong> {urlParams.unitNumber || 'Loading...'}</p>
                <p style={{margin: '5px 0', color: '#333'}}><strong>Address:</strong> {urlParams.address || 'Loading...'}</p>
                <p style={{margin: '5px 0', color: '#333'}}><strong>Monthly Rent:</strong> ${urlParams.monthlyRent || '0'}</p>
              </div>
              {urlParams.applicantId && unitDetails && (
                <p style={{ color: '#166534', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, padding: '10px 12px', marginBottom: 16, fontSize: 14, textAlign: 'center' }}>
                  We filled in your <strong>name</strong>, <strong>email</strong>, and <strong>phone</strong> from your approved application. Add a <strong>password</strong> below to finish—your email must stay the same so we can link your account.
                </p>
              )}
              {(!urlParams.applicantId || !unitDetails) && (
                <p style={{ color: '#64748b', fontSize: 13, marginBottom: 12, textAlign: 'center' }}>
                  {unitDetails ? 'Confirm your details, then choose a password.' : 'Loading your details…'}
                </p>
              )}
              <label className="input-label-tenant" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Full name</label>
              <input className="input-field" type="text" placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} readOnly={readOnlyPrefilled(name)} style={readOnlyPrefilled(name) ? readOnlyInputStyle : undefined} title={readOnlyPrefilled(name) ? 'From your application' : undefined} />
              <label className="input-label-tenant" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, marginTop: 8 }}>Phone</label>
              <input className="input-field" type="text" placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} readOnly={readOnlyPrefilled(phone)} style={readOnlyPrefilled(phone) ? readOnlyInputStyle : undefined} title={readOnlyPrefilled(phone) ? 'From your application' : undefined} />
              <label className="input-label-tenant" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, marginTop: 8 }}>Email</label>
              <input className="input-field" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} readOnly={readOnlyPrefilled(email)} style={readOnlyPrefilled(email) ? readOnlyInputStyle : undefined} title={readOnlyPrefilled(email) ? 'Must match your application—we use it to link your account' : undefined} />
              <label className="input-label-tenant" style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4, marginTop: 8 }}>Choose a password</label>
              <input className="input-field" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
              <button type="submit" className="btn-primary" disabled={!unitDetails} style={{ marginTop: 16 }}>
                {unitDetails ? 'Create Account' : 'Loading unit details...'}
              </button>
              <p className="toggle-link">
                Already have an account?
                <span onClick={() => {
                  setIsNewUser(false);
                  setRole('tenant');
                  setEmail('');
                  setPassword('');
                  setName('');
                  setPhone('');
                }}>Login here</span>
              </p>
            </form>
          ) : (
            <>
              {isNewUser && role === 'tenant' && (!urlParams.unitId || !urlParams.ownerId) && (
                <div style={{background: '#f8d7da', border: '1px solid #f5c6cb', color: '#721c24', padding: '15px', borderRadius: '5px', marginBottom: '20px', textAlign: 'center'}}>
                  <p style={{margin: 0, fontWeight: 'bold'}}>Invalid Access</p>
                  <p style={{margin: '5px 0 0 0', fontSize: '14px'}}>Tenants must sign up using an owner's invite link. Please ask your property owner for the link.</p>
                </div>
              )}
              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (isNewUser) {
                    await handleSignUp();
                  } else {
                    await handleLogin();
                  }
                }}
              >
                <h2 className="form-header">{isNewUser ? 'Create Account' : 'Login'}</h2>
                {!isNewUser && authMessage && (
                  <div className={`auth-message auth-message-${authMessageType}`}>
                    {authMessage}
                  </div>
                )}
                {isNewUser && (
                  <>
                    {staffInviteSignup ? (
                      <div
                        style={{
                          marginBottom: 12,
                          padding: '10px 12px',
                          borderRadius: 10,
                          background: '#eef2ff',
                          border: '1px solid #c7d2fe',
                          color: '#1e3a8a',
                          fontWeight: 700,
                          fontSize: 14,
                          textAlign: 'center',
                        }}
                      >
                        Invited team member (Owner Portal access)
                      </div>
                    ) : (
                      <select
                        className="select-field"
                        value={role}
                        onChange={(e) => {
                          setRole(e.target.value);
                          if (e.target.value === 'owner') {
                            setIsNewUser(true);
                          }
                        }}
                      >
                        <option value="professional">Service Professional</option>
                        <option value="owner">Home Owner</option>
                        <option value="realtor">Realtor</option>
                      </select>
                    )}
                    <input className="input-field" type="text" placeholder="Full Name" value={name} onChange={(e) => setName(e.target.value)} />
                    {role === 'professional' && (
                      <input className="input-field" type="text" placeholder="Company Name" value={unit} onChange={(e) => setUnit(e.target.value)} />
                    )}
                    {role === 'realtor' && (
                      <input className="input-field" type="text" placeholder="License Number" value={unit} onChange={(e) => setUnit(e.target.value)} />
                    )}
                    <input className="input-field" type="text" placeholder="Phone Number" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </>
                )}
                <input className="input-field" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
                <input className="input-field" type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                {!isNewUser && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <label className="remember-me-row" style={{ margin: 0 }}>
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <span>Remember me</span>
                    </label>
                    {loginError && (
                      <button
                        type="button"
                        className="text-action-button"
                        style={{ margin: 0 }}
                        onClick={handlePasswordReset}
                      >
                        Forgot password?
                      </button>
                    )}
                  </div>
                )}
                <button type="submit" className="btn-primary">{isNewUser ? 'Get Started' : 'Sign In'}</button>
                <p className="toggle-link">
                  {isNewUser ? "Already have an account?" : "No account?"}
                  <span onClick={() => {
                    setIsNewUser(!isNewUser);
                    setAuthMessage('');
                    setAuthMessageType('');
                    setLoginError(false);
                    setRole(hasStaffInviteQuery ? 'owner' : 'professional');
                    setEmail('');
                    setPassword('');
                    setName('');
                    setPhone('');
                    setUnit('');
                    setOwnerSignupStage(1);
                    setFirstName('');
                    setLastName('');
                    setHomeAddress('');
                    setNumUnits('');
                    setUnits([]);
                  }}>{isNewUser ? "Login here" : "Register here"}</span>
                </p>
              </form>
            </>
          )}
            </div>
            <p className="auth-trust">
              <span aria-hidden>🔒</span>
              <span>Secure sign-in</span>
              <span className="auth-trust-dot" aria-hidden>•</span>
              <span>tenantdispatch.com</span>
            </p>
          </div>
        </div>
      );
    }
  };

  const OwnerDeepLinkCapture = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    useEffect(() => {
      const rid = searchParams.get('requestId')?.trim();
      if (rid) {
        try {
          sessionStorage.setItem(PENDING_OWNER_OPEN_MAINTENANCE_REQUEST_KEY, rid);
        } catch (_) {
          /* ignore */
        }
        setRole('owner');
        setShowAuth(true);
      }
      navigate('/', { replace: true });
      if (window.location.hash !== '#auth') {
        window.history.replaceState(null, '', '/#auth');
      }
    }, [searchParams, navigate]);
    return (
      <div style={{ padding: 32, textAlign: 'center', color: '#64748b', fontSize: 15 }}>
        Opening sign-in…
      </div>
    );
  };

  // Routes will handle display
  return (
    <>
      <Routes>
        <Route path="/about" element={<AboutUs />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/subprocessors" element={<SubprocessorsPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route
          path="/login"
          element={user ? <Navigate to="/" replace /> : renderContent()}
        />
        <Route path="/admin" element={
          user && userData?.role === 'owner' && userData?.isSuperAdmin ? (
            <div className="App">
              <AdminDashboard 
                user={user} 
                userData={userData} 
                onLogout={() => signOut(auth)}
              />
            </div>
          ) : <Navigate to="/" />
        } />
        <Route path="/owner" element={
          user && userData?.role === 'owner' && !userData?.isSuperAdmin ? (
            <div className="App">
              <OwnerDashboard
                user={user}
                userData={userData}
                onProfileUpdate={() => refreshUserData(user.uid)}
                onLogout={() => signOut(auth)}
              />
            </div>
          ) : user ? (
            <Navigate to="/" replace />
          ) : (
            <OwnerDeepLinkCapture />
          )
        } />
        <Route path="/owner/accept-invite/:invitationId" element={<AcceptInvitationPage />} />
        <Route path="/owner/staff-invite" element={<OwnerStaffInvitePage />} />
        <Route path="/owner/rate-job/:requestId" element={<OwnerRateProfessionalPage />} />
        {/* Removed /owner/tenants route: TenantListPage is now an OwnerDashboard subview */}
        <Route path="/professional" element={
          user && userData?.role === 'professional' ? (
            <div className="App">
              <ProfessionalDashboard 
                user={user} 
                userData={userData} 
                onProfileUpdate={() => refreshUserData(user.uid)}
                onLogout={() => signOut(auth)}
              />
            </div>
          ) : (
            <ProfessionalInviteEntryRedirect user={user} userData={userData} />
          )
        } />
        <Route path="/realtor" element={
          user && userData?.role === 'realtor' ? (
            <div className="App">
              <RealtorDashboard 
                user={user} 
                userData={userData} 
                onProfileUpdate={() => refreshUserData(user.uid)}
                onLogout={() => signOut(auth)}
              />
            </div>
          ) : <Navigate to="/" />
        } />
        <Route path="/tenant" element={
          user && userData?.role === 'tenant' ? (
            <div className="App">
              <TenantDashboard 
                user={user} 
                userData={userData} 
                onProfileUpdate={() => refreshUserData(user.uid)}
                onLogout={() => signOut(auth)}
              />
            </div>
          ) : <Navigate to="/" />
        } />
        <Route path="/for-realtors" element={<RealtorLandingPage />} />
        <Route path="/for-professionals" element={<ProfessionalLandingPage />} />
        <Route path="/for-professionals/join" element={<ProfessionalJoinWizard />} />
        <Route path="*" element={
          user ? renderContent() :
          shouldShowAuthForm ? renderContent() :
          <LandingPage onGetStarted={(mode) => {
            if (window.location.hash !== '#auth') {
              window.history.pushState(null, '', '#auth');
            }
            setShowAuth(true);
            if (mode === 'owner-signup') {
              setIsNewUser(true);
              setRole('owner');
              setEmail('');
              setPassword('');
              setName('');
              setPhone('');
              setUnit('');
            } else if (mode === 'realtor-signup') {
              setIsNewUser(true);
              setRole('realtor');
              setEmail('');
              setPassword('');
              setName('');
              setPhone('');
              setUnit('');
            } else if (mode === 'professional-signup') {
              setIsNewUser(true);
              setRole('professional');
              setEmail('');
              setPassword('');
              setName('');
              setPhone('');
              setUnit('');
            }
          }} />
        } />
      </Routes>

      {/* Floating Feedback Button */}
      {user && (
        <>
          <button
            onClick={() => setShowFeedbackModal(true)}
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              width: '56px',
              height: '56px',
              borderRadius: '50%',
              background: '#1e293b',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              fontSize: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 999,
              transition: 'transform 0.2s, box-shadow 0.2s',
              hover: {
                transform: 'scale(1.1)',
                boxShadow: '0 6px 16px rgba(0,0,0,0.2)'
              }
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.1)';
              e.target.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
            }}
            title="Send feedback"
          >
            💬
          </button>

          {/* Feedback Modal */}
          {showFeedbackModal && (
            <FeedbackModal 
              user={user} 
              userData={userData}
              onClose={() => setShowFeedbackModal(false)}
            />
          )}
        </>
      )}
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <Router>
        <AppContent />
      </Router>
    </HelmetProvider>
  );
}

export default App;
